// ---------------------------------------------------------------------------
// Enums mirroring the Java backend
// ---------------------------------------------------------------------------

export enum FieldType {
  STRING  = 'STRING',
  NUMERIC = 'NUMERIC',
  DATE    = 'DATE',
}

export enum FilterOperator {
  EQ          = 'EQ',
  NEQ         = 'NEQ',
  CONTAINS    = 'CONTAINS',
  STARTS_WITH = 'STARTS_WITH',
  ENDS_WITH   = 'ENDS_WITH',
  LT          = 'LT',
  LTE         = 'LTE',
  GT          = 'GT',
  GTE         = 'GTE',
  BETWEEN     = 'BETWEEN',
}

// ---------------------------------------------------------------------------
// Operator display labels (mathematical symbols where available)
// ---------------------------------------------------------------------------

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  [FilterOperator.EQ]:          '=',
  [FilterOperator.NEQ]:         '≠',
  [FilterOperator.LT]:          '<',
  [FilterOperator.LTE]:         '≤',
  [FilterOperator.GT]:          '>',
  [FilterOperator.GTE]:         '≥',
  [FilterOperator.BETWEEN]:     '≤ x ≤',
  [FilterOperator.CONTAINS]:    '∋',
  [FilterOperator.STARTS_WITH]: '^',
  [FilterOperator.ENDS_WITH]:   '$',
};

export const OPERATOR_TOOLTIPS: Record<FilterOperator, string> = {
  [FilterOperator.EQ]:          'Equals',
  [FilterOperator.NEQ]:         'Not equal',
  [FilterOperator.LT]:          'Less than',
  [FilterOperator.LTE]:         'Less than or equal',
  [FilterOperator.GT]:          'Greater than',
  [FilterOperator.GTE]:         'Greater than or equal',
  [FilterOperator.BETWEEN]:     'Between (inclusive)',
  [FilterOperator.CONTAINS]:    'Contains (substring)',
  [FilterOperator.STARTS_WITH]: 'Starts with',
  [FilterOperator.ENDS_WITH]:   'Ends with',
};

export interface OperatorOption {
  label:   string;
  tooltip: string;
  value:   FilterOperator;
}

const STRING_OPERATORS: OperatorOption[] = [
  FilterOperator.EQ, FilterOperator.NEQ,
  FilterOperator.CONTAINS, FilterOperator.STARTS_WITH, FilterOperator.ENDS_WITH,
].map(op => ({ label: OPERATOR_LABELS[op], tooltip: OPERATOR_TOOLTIPS[op], value: op }));

const NUMERIC_OPERATORS: OperatorOption[] = [
  FilterOperator.EQ, FilterOperator.NEQ,
  FilterOperator.LT, FilterOperator.LTE,
  FilterOperator.GT, FilterOperator.GTE,
  FilterOperator.BETWEEN,
].map(op => ({ label: OPERATOR_LABELS[op], tooltip: OPERATOR_TOOLTIPS[op], value: op }));

const DATE_OPERATORS: OperatorOption[] = [
  FilterOperator.EQ,
  FilterOperator.LT, FilterOperator.LTE,
  FilterOperator.GT, FilterOperator.GTE,
  FilterOperator.BETWEEN,
].map(op => ({ label: OPERATOR_LABELS[op], tooltip: OPERATOR_TOOLTIPS[op], value: op }));

export function getOperatorsForFieldType(fieldType: FieldType): OperatorOption[] {
  switch (fieldType) {
    case FieldType.NUMERIC: return NUMERIC_OPERATORS;
    case FieldType.DATE:    return DATE_OPERATORS;
    default:                return STRING_OPERATORS;
  }
}

// ---------------------------------------------------------------------------
// MetadataFieldDef — mirrors the Java MetadataField enum
// ---------------------------------------------------------------------------

export interface MetadataFieldDef {
  key:         string;
  label:       string;
  category:    string;
  fieldType:   FieldType;
  description: string;
}

export const METADATA_FIELDS: MetadataFieldDef[] = [
  // Acquisition & Hardware
  { key: 'OBJECTIVE_MAGNIFICATION',   label: 'Magnification',          category: 'Acquisition & Hardware',       fieldType: FieldType.NUMERIC, description: 'Objective lens magnification (e.g. 10, 20, 40)' },
  { key: 'OBJECTIVE_NA',              label: 'Numerical Aperture',      category: 'Acquisition & Hardware',       fieldType: FieldType.NUMERIC, description: 'Numerical aperture of the objective lens' },
  { key: 'OBJECTIVE_MODEL',           label: 'Objective Model',         category: 'Acquisition & Hardware',       fieldType: FieldType.STRING,  description: 'Objective model name (e.g. Plan Apo)' },
  { key: 'OBJECTIVE_MANUFACTURER',    label: 'Objective Manufacturer',  category: 'Acquisition & Hardware',       fieldType: FieldType.STRING,  description: 'Objective manufacturer (e.g. Nikon, Leica)' },
  { key: 'MICROSCOPE_TYPE',           label: 'Microscope Type',         category: 'Acquisition & Hardware',       fieldType: FieldType.STRING,  description: 'Microscope type (e.g. Widefield, Confocal)' },
  { key: 'MICROSCOPE_MODEL',          label: 'Microscope Model',        category: 'Acquisition & Hardware',       fieldType: FieldType.STRING,  description: 'Microscope model name' },
  { key: 'DETECTOR_TYPE',             label: 'Detector Type',           category: 'Acquisition & Hardware',       fieldType: FieldType.STRING,  description: 'Detector type (e.g. CCD, EMCCD, sCMOS)' },
  // Dimensions & Spatial Resolution
  { key: 'SIZE_X',                    label: 'Width (px)',              category: 'Dimensions & Resolution',      fieldType: FieldType.NUMERIC, description: 'Image width in pixels (SizeX)' },
  { key: 'SIZE_Y',                    label: 'Height (px)',             category: 'Dimensions & Resolution',      fieldType: FieldType.NUMERIC, description: 'Image height in pixels (SizeY)' },
  { key: 'SIZE_Z',                    label: 'Z-planes',                category: 'Dimensions & Resolution',      fieldType: FieldType.NUMERIC, description: 'Number of Z-slices (SizeZ)' },
  { key: 'SIZE_C',                    label: 'Channels',                category: 'Dimensions & Resolution',      fieldType: FieldType.NUMERIC, description: 'Number of channels (SizeC)' },
  { key: 'SIZE_T',                    label: 'Timepoints',              category: 'Dimensions & Resolution',      fieldType: FieldType.NUMERIC, description: 'Number of timepoints (SizeT)' },
  { key: 'PIXELS_TYPE',               label: 'Pixel Type',              category: 'Dimensions & Resolution',      fieldType: FieldType.STRING,  description: 'Pixel type (e.g. uint8, uint16, float32)' },
  { key: 'PHYSICAL_SIZE_X_UM',        label: 'Physical Size X (µm)',    category: 'Dimensions & Resolution',      fieldType: FieldType.NUMERIC, description: 'Physical pixel size X in µm/px' },
  { key: 'PHYSICAL_SIZE_Y_UM',        label: 'Physical Size Y (µm)',    category: 'Dimensions & Resolution',      fieldType: FieldType.NUMERIC, description: 'Physical pixel size Y in µm/px' },
  { key: 'PHYSICAL_SIZE_Z_UM',        label: 'Physical Size Z (µm)',    category: 'Dimensions & Resolution',      fieldType: FieldType.NUMERIC, description: 'Physical Z step size in µm' },
  // Biological & Time Series
  { key: 'EXPERIMENT_TYPE',           label: 'Experiment Type',         category: 'Biological & Time Series',     fieldType: FieldType.STRING,  description: 'Experiment type (e.g. TimeLapse, FISH, FRAP)' },
  { key: 'EXPERIMENT_DESCRIPTION',    label: 'Experiment Description',  category: 'Biological & Time Series',     fieldType: FieldType.STRING,  description: 'Experiment description text' },
  { key: 'TIME_INCREMENT_S',          label: 'Time Increment (s)',      category: 'Biological & Time Series',     fieldType: FieldType.NUMERIC, description: 'Time interval between frames in seconds' },
  { key: 'EXPOSURE_TIME_MS',          label: 'Exposure Time (ms)',      category: 'Biological & Time Series',     fieldType: FieldType.NUMERIC, description: 'Plane exposure time in milliseconds' },
  // Channels & Channel Settings
  { key: 'CHANNEL_NAME',              label: 'Channel Name',            category: 'Channels & Channel Settings',  fieldType: FieldType.STRING,  description: 'Channel name — matches any channel' },
  { key: 'CHANNEL_FLUOR',             label: 'Fluorophore',             category: 'Channels & Channel Settings',  fieldType: FieldType.STRING,  description: 'Fluorophore name — matches any channel' },
  { key: 'CHANNEL_EXCITATION_WL_NM',  label: 'Excitation λ (nm)',       category: 'Channels & Channel Settings',  fieldType: FieldType.NUMERIC, description: 'Excitation wavelength in nm — any channel' },
  { key: 'CHANNEL_EMISSION_WL_NM',    label: 'Emission λ (nm)',         category: 'Channels & Channel Settings',  fieldType: FieldType.NUMERIC, description: 'Emission wavelength in nm — any channel' },
  { key: 'CHANNEL_ILLUMINATION_TYPE', label: 'Illumination Type',       category: 'Channels & Channel Settings',  fieldType: FieldType.STRING,  description: 'Illumination type (e.g. Epifluorescence, Transmitted)' },
  { key: 'CHANNEL_CONTRAST_METHOD',   label: 'Contrast Method',         category: 'Channels & Channel Settings',  fieldType: FieldType.STRING,  description: 'Contrast method (e.g. Fluorescence, Brightfield)' },
  // Annotation & Organisation
  { key: 'IMAGE_NAME',                label: 'Image Name',              category: 'Annotation & Organisation',    fieldType: FieldType.STRING,  description: 'Image or series name embedded in metadata' },
  { key: 'IMAGE_DESCRIPTION',         label: 'Image Description',       category: 'Annotation & Organisation',    fieldType: FieldType.STRING,  description: 'Description text embedded in image metadata' },
  // Date, Time & Provenance
  { key: 'ACQUISITION_DATE',          label: 'Acquisition Date',        category: 'Date, Time & Provenance',      fieldType: FieldType.DATE,    description: 'Acquisition date and time (ISO-8601)' },
  // File
  { key: 'FILE_SIZE_BYTES',           label: 'File Size (bytes)',       category: 'File',                         fieldType: FieldType.NUMERIC, description: 'Raw file size in bytes' },
];

/** Group shape used with PrimeNG grouped dropdown ([group]="true"). */
export interface MetadataFieldGroup {
  label: string;
  items: MetadataFieldDef[];
}
export const GROUPED_METADATA_FIELDS: MetadataFieldGroup[] = (() => {
  const map = new Map<string, MetadataFieldDef[]>();
  for (const f of METADATA_FIELDS) {
    if (!map.has(f.category)) map.set(f.category, []);
    map.get(f.category)!.push(f);
  }
  return Array.from(map.entries()).map(([label, items]) => ({ label, items }));
})();

// ---------------------------------------------------------------------------
// MetadataFilter — the DTO sent to the backend
// ---------------------------------------------------------------------------

export interface MetadataFilter {
  field:          string;
  operator:       FilterOperator;
  value:          unknown;
  value2?:        unknown;
  caseSensitive?: boolean;
}

// ---------------------------------------------------------------------------
// MetadataFilterDraft — working model for an in-progress row in the UI
// ---------------------------------------------------------------------------

export interface MetadataFilterDraft {
  field:    MetadataFieldDef | null;
  operator: FilterOperator   | null;
  value:    unknown;
  value2:   unknown;
}

export function emptyDraft(): MetadataFilterDraft {
  return { field: null, operator: null, value: null, value2: null };
}

/** Returns true when the draft has enough information to be sent as a filter. */
export function isDraftComplete(draft: MetadataFilterDraft): boolean {
  if (!draft.field || !draft.operator) return false;
  const hasValue = draft.value !== null && draft.value !== undefined && draft.value !== '';
  if (!hasValue) return false;
  if (draft.operator === FilterOperator.BETWEEN) {
    return draft.value2 !== null && draft.value2 !== undefined && draft.value2 !== '';
  }
  return true;
}

/** Converts a complete draft to a backend MetadataFilter. */
export function draftToFilter(draft: MetadataFilterDraft, caseSensitive: boolean): MetadataFilter {
  const filter: MetadataFilter = {
    field:         draft.field!.key,
    operator:      draft.operator!,
    value:         formatValue(draft.value, draft.field!.fieldType),
    caseSensitive,
  };
  if (draft.operator === FilterOperator.BETWEEN) {
    filter.value2 = formatValue(draft.value2, draft.field!.fieldType);
  }
  return filter;
}

/**
 * Converts a Date object from p-calendar to the ISO-8601 local string the
 * backend expects (e.g. "2018-07-16T14:30:00" — no timezone offset).
 */
function formatValue(value: unknown, fieldType: FieldType): unknown {
  if (fieldType === FieldType.DATE && value instanceof Date) {
    const pad = (n: number): string => String(n).padStart(2, '0');
    return (
      `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}` +
      `T${pad(value.getHours())}:${pad(value.getMinutes())}:${pad(value.getSeconds())}`
    );
  }
  return value;
}
